import { useState, type MouseEvent } from "react";
import moment from "moment/moment";
import { IconChevronDown } from "@tabler/icons-react";
import DropdownMenu from "../UI/DropdownMenu";
import Status from "../UI/Status";
import Button from "../UI/Button";
import Text from "../UI/Text";
import VisitDateDialog from "./VisitDateDialog";
import {
  getLandlordInterestOption,
  getVisitStatusOption,
  LANDLORD_INTEREST_OPTIONS,
  VISIT_STATUS_OPTIONS,
  type FollowUp,
} from "../../utils/candidature";
import type { Tenant, VisitStatus } from "../../models/tenant";
import styles from "./SuiviMenus.module.css";

interface SuiviMenuProps {
  tenant: Tenant;
  onChange: (data: FollowUp) => void;
}

/** Statuts de visite pour lesquels on propose une date au moment du choix. */
const DATED_STATUSES: VisitStatus[] = ["to_schedule", "scheduled"];

export function InterestMenu({ tenant, onChange }: SuiviMenuProps) {
  const current = getLandlordInterestOption(tenant.landlord_interest);
  return (
    <DropdownMenu
      trigger={
        <div className={styles.trigger}>
          <Status color={current.color} text={current.label} />
          <IconChevronDown size={14} />
        </div>
      }
      nativeButton={false}
      align="start"
    >
      {LANDLORD_INTEREST_OPTIONS.map((option) => (
        <DropdownMenu.Item
          key={option.value}
          label={option.label}
          onClick={() => onChange({ landlord_interest: option.value || null })}
          requiresWrite
        />
      ))}
    </DropdownMenu>
  );
}

export function VisitMenu({ tenant, onChange }: SuiviMenuProps) {
  const [pendingStatus, setPendingStatus] = useState<VisitStatus | null>(null);
  const current = getVisitStatusOption(tenant.visit_status);

  function pick(value: VisitStatus | "") {
    if (value && DATED_STATUSES.includes(value)) {
      setPendingStatus(value);
      return;
    }
    onChange({
      visit_status: value || null,
      visit_date: value ? tenant.visit_date : null,
    });
  }

  return (
    <div
      className={styles.visitLine}
      onClick={(event: MouseEvent) => event.stopPropagation()}
    >
      <DropdownMenu
        trigger={
          <div className={styles.trigger}>
            <Status color={current.color} text={current.label} />
            <IconChevronDown size={14} />
          </div>
        }
        nativeButton={false}
        align="start"
      >
        {VISIT_STATUS_OPTIONS.map((option) => (
          <DropdownMenu.Item
            key={option.value}
            label={option.label}
            onClick={() => pick(option.value)}
            requiresWrite
          />
        ))}
      </DropdownMenu>
      {tenant.visit_status && tenant.visit_date && (
        <Button
          type="button"
          variant="tertiary"
          size="sm"
          requiresWrite
          onClick={() => setPendingStatus(tenant.visit_status ?? null)}
        >
          {moment(tenant.visit_date).format("DD/MM HH:mm")}
        </Button>
      )}
      <VisitDateDialog
        open={pendingStatus !== null}
        onOpenChange={(open) => {
          if (!open) setPendingStatus(null);
        }}
        initialDate={tenant.visit_date}
        onSubmit={(date) => {
          onChange({ visit_status: pendingStatus, visit_date: date });
          setPendingStatus(null);
        }}
      />
    </div>
  );
}

/** Bloc « Avis » + « Visite » avec libellés alignés, pour la liste et la carte mobile. */
export function SuiviBlock({ tenant, onChange }: SuiviMenuProps) {
  return (
    <div className={styles.block}>
      <Text as="span" variant="deci">
        Avis
      </Text>
      <InterestMenu tenant={tenant} onChange={onChange} />
      <Text as="span" variant="deci">
        Visite
      </Text>
      <VisitMenu tenant={tenant} onChange={onChange} />
    </div>
  );
}
